import {
  DEFAULT_API_ENDPOINT,
  createRoutedGraphQLClient,
  isUnauthenticatedResponse,
  type ApiEndpointName,
  type RoutedGraphQLClientOptions,
  type RoutedGraphQLRequest,
} from '../routing/apiRouting';
import { resolveGraphQLEndpoints, type RuntimeEnvironment } from '../config/runtimeConfig';
import { createAuthHeadersProvider, type AuthHeadersOptions } from './authHeaders';

type ClientGraphQLClientOptions = {
  env: RuntimeEnvironment;
  defaultEndpoint?: ApiEndpointName;
  fetchImpl?: RoutedGraphQLClientOptions['fetchImpl'];
  // Attaches `Authorization: Bearer <token>` when provided. Omit for the
  // token-less client used to obtain the token in the first place (login).
  auth?: AuthHeadersOptions;
  // Called when a request comes back unauthenticated (401 / UNAUTHENTICATED) —
  // signals that the stored token is missing, expired, or revoked so the
  // caller can clear the session and return the user to login.
  onUnauthorized?: () => void;
};

export const createClientGraphQLClient = ({
  env,
  defaultEndpoint = DEFAULT_API_ENDPOINT,
  fetchImpl,
  auth,
  onUnauthorized,
}: ClientGraphQLClientOptions) => {
  const endpoints = resolveGraphQLEndpoints(env);

  const client = createRoutedGraphQLClient({
    endpoints,
    defaultEndpoint,
    fetchImpl,
    getHeaders: auth ? createAuthHeadersProvider(auth) : undefined,
  });

  if (!onUnauthorized) {
    return client;
  }

  return {
    request: async <TData>(request: RoutedGraphQLRequest): Promise<TData> => {
      try {
        return await client.request<TData>(request);
      } catch (error) {
        if (isUnauthenticatedResponse(error)) {
          onUnauthorized();
        }
        throw error;
      }
    },
  };
};
